import Image from 'next/image';
import React, { useState, useEffect, useRef } from 'react';

interface Props {
  images: string[];
  autoSlide?: boolean;
  autoSlideInterval?: number;
}

const MobileProductSlider: React.FC<Props> = ({ images, autoSlide = true, autoSlideInterval = 3000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [startX, setStartX] = useState<number | null>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (autoSlide && !isSwiping) {
      const slideInterval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
      }, autoSlideInterval);
      return () => clearInterval(slideInterval);
    }
  }, [autoSlide, autoSlideInterval, images.length, isSwiping]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setStartX(e.touches[0].clientX);
    setIsSwiping(true);
    if (autoSlide) {
      let intervals = window.setInterval(() => {}, 0);
      while (intervals--) window.clearInterval(intervals);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!startX) return;
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
      } else {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
      }
      setStartX(null);
    }
  };

  const handleTouchEnd = () => {
    setIsSwiping(false);
    setStartX(null);
  };

  return (
    <div 
      className="relative w-full max-w-3xl aspect-square mx-auto"
      ref={sliderRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      dir="rtl"
    >
      <div className="overflow-hidden relative w-full h-full">
        {images.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-transform duration-300 transform ${
              index === currentIndex ? 'translate-x-0' : 
              index < currentIndex ? 'translate-x-full' : '-translate-x-full'
            }`}
          >
            <Image
              src={`${process.env.NEXT_PUBLIC_CDN_URL}/${image}`}
              alt={`Slide ${index}`}
              fill
              className="select-none"
              draggable={false}
              priority
              sizes="100vw"
              style={{
                objectFit: 'cover',
                objectPosition: 'center center'
              }}
            />
          </div>
        ))}
      </div>
      <div className="absolute bottom-4 left-0 right-0 flex justify-center">
        {images.map((_, index) => (
          <div
            key={index}
            className={`w-4 h-4 rounded-full mx-1 cursor-pointer transition-colors duration-200 ${
              index === currentIndex ? 'bg-primary-500' : 'bg-[#EFEFEF]'
            }`}
            onClick={() => setCurrentIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default MobileProductSlider;